'use strict';

// ── Clasificación de Postura y Actividad Polar H10 ────────────────────────────
// El sensor Polar H10 se ubica sobre el esternón (pecho):
//   - Eje Y: vertical (a lo largo del torso/columna).
//   - Eje X: transversal (horizontal lateral de derecha a izquierda).
//   - Eje Z: anteroposterior (perpendicular al pecho, hacia afuera).

export const POSTURE_TYPES = [
  { key: 'sentado',       label: 'Sentado',       iconType: 'sitting',  color: '#ff3b30' },
  { key: 'parado',        label: 'Parado',        iconType: 'standing', color: '#007aff' },
  { key: 'en_movimiento', label: 'En movimiento', iconType: 'walking',  color: '#ffd60a' },
  { key: 'acostado',      label: 'Acostado',      iconType: 'lying',    color: '#8e8e93' },
];

export function getPostureColor(key) {
  switch (key) {
    case 'sentado': return '#ff3b30';
    case 'en_movimiento':
    case 'caminando': return '#ffd60a';
    case 'parado':
    case 'parado_sentado': return '#007aff';
    case 'desconocido': return '#aeaeb2';
    case 'acostado':
    case 'otro':
    default:
      return '#8e8e93';
  }
}

export function getPostureLabel(key) {
  switch (key) {
    case 'sentado': return 'Sentado';
    case 'parado':
    case 'parado_sentado': return 'Parado';
    case 'en_movimiento':
    case 'caminando': return 'En movimiento';
    case 'desconocido': return 'Desconocido';
    case 'acostado': return 'Acostado';
    case 'otro':
    default:
      return 'Acostado';
  }
}

export function formatPostureSec(s) {
  if (!s || s < 1) return '0 s';
  if (s < 60) return `${Math.round(s)} s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  if (m < 60) {
    return sec > 0 ? `${m}m ${sec}s` : `${m} min`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h} h`;
}

/**
 * Clasifica una muestra instantánea a partir de acelerometría y movimiento dinámico.
 *
 * Variables de entrada:
 *  - x, y, z: medias de aceleración en mg
 *  - motionRms: desviación estándar (RMS dinámico) de la aceleración respecto a la gravedad en mg.
 *    * Sentado en reposo: motionRms < 14 mg (casi inmovilidad total al apoyar el cuerpo).
 *    * Parado quieto (balanceo postural de pie): motionRms 14 mg - 32 mg.
 *    * En movimiento (caminata, trote, desplazamientos): motionRms >= 32 mg.
 *  - isStepping: true si el contador de pasos registró incremento.
 *  - sitImpact: true si hubo impacto de sentarse (deceleración súbita) y ahora calma.
 *  - hr: pulso actual en bpm.
 */
export function classifyInstantPosture({
  x = 0,
  y = 0,
  z = 0,
  motionRms = 0,
  jerk = 0,
  isStepping = false,
  sitImpact = false,
  hr = null,
}) {
  const effectiveMotion = motionRms > 0 ? motionRms : jerk;
  const mag = Math.sqrt(x * x + y * y + z * z) || 244;

  // Umbral dinámico de movimiento calibrado según escala (escala 244 vs 1000)
  const isHighScale = mag > 500;
  const motionThreshMoving = isHighScale ? 32 : 8;
  const motionThreshQuiet  = isHighScale ? 14 : 3.5;

  // 1. En movimiento: pasos detectados o vibración dinámica de desplazamiento
  // Validación fisiológica: si el pulso está en reposo (< 68 bpm) y no hay pasos registrados, es reposo estático (sentado)
  const isRestingHr = hr != null && hr < 68;
  if (isStepping || (effectiveMotion >= motionThreshMoving && !isRestingHr)) {
    return 'en_movimiento';
  }

  // Componente vertical del esternón (invariante: toma el eje con mayor componente de gravedad |X| o |Y|)
  const vert = Math.max(Math.abs(x), Math.abs(y));
  const tiltDeg = Math.acos(Math.min(1, Math.max(0, vert / mag))) * (180 / Math.PI);

  // 4. Acostado: decúbito horizontal plano (acostado boca arriba o boca abajo en cama, tilt > 68°)
  if (tiltDeg > 68) {
    return 'acostado';
  }

  // 2. Sentado:
  // - Impacto de sentarse
  // - O reclinación en respaldo de silla o sillón (tilt entre 18° y 68°)
  // - O inmovilidad en reposo característica de estar sentado (effectiveMotion < motionThreshQuiet)
  if (sitImpact || tiltDeg >= 18 || effectiveMotion < motionThreshQuiet) {
    return 'sentado';
  }

  // 3. Parado:
  // Torso vertical erguido (tilt < 18°) con balanceo postural propio de estar de pie
  if (tiltDeg < 18) {
    return 'parado';
  }

  return 'acostado';
}

/**
 * Genera el desglose con porcentajes y valores formateados para una sesión
 */
export function computePostureBreakdown(secondsMap) {
  const map = secondsMap || {};

  const items = POSTURE_TYPES.map(p => {
    let s = map[p.key] || 0;
    if (p.key === 'parado') s += (map.parado_sentado || 0);
    if (p.key === 'en_movimiento') s += (map.caminando || 0);
    if (p.key === 'acostado') s += (map.otro || 0); // 'acostado' ya está en s; sólo sumamos 'otro'
    return { ...p, seconds: s, formatted: formatPostureSec(s) };
  }).filter(p => p.key !== 'acostado' || p.seconds > 0);

  // Total CONSISTENTE = suma de las categorías ya fusionadas (no del map crudo, que
  // contaba 'otro'/'acostado' de más). Así los porcentajes cierran siempre en 100.
  const total = items.reduce((a, p) => a + p.seconds, 0);

  // Porcentajes con método del resto mayor: redondear cada parte por separado puede
  // dar 99% o 101%. Repartimos los puntos que faltan a las categorías con mayor resto
  // para que la suma dé SIEMPRE 100% exacto (y nunca más de 100).
  return assignExactPercentages(items, total);
}

// Reparte porcentajes enteros que suman exactamente 100 (largest remainder).
function assignExactPercentages(items, total) {
  if (!total || total <= 0) return items.map(p => ({ ...p, pct: 0 }));
  const exact = items.map(p => (p.seconds / total) * 100);
  const floors = exact.map(Math.floor);
  let remaining = 100 - floors.reduce((a, b) => a + b, 0);
  // Candidatos a recibir +1: los de mayor parte decimal, sólo entre los que tienen tiempo.
  const order = items
    .map((p, i) => ({ i, rem: exact[i] - floors[i], hasTime: p.seconds > 0 }))
    .filter(o => o.hasTime)
    .sort((a, b) => b.rem - a.rem);
  const bump = new Set();
  for (let k = 0; k < order.length && remaining > 0; k++, remaining--) bump.add(order[k].i);
  return items.map((p, i) => ({ ...p, pct: floors[i] + (bump.has(i) ? 1 : 0) }));
}

/**
 * Analiza un conjunto histórico de lecturas / acelerómetro para una sesión
 */
export function analyzeSessionPosture(accData, readings, totalSteps = 0) {
  if (!readings || !readings.length) {
    return null;
  }

  // Fecha oficial de inicio de grabación de acelerómetro: 29 de septiembre de 2026 a las 23:40 hs
  const ACC_START_DATE = new Date('2026-09-29T23:40:00-03:00').getTime();
  const sessionStart = readings[0]?.time || 0;
  const durSec = Math.max(1, Math.round((readings[readings.length - 1].time - readings[0].time) / 1000));

  // Antes del 29/09/2026 NO existía grabación de acelerómetro: no inventar posturas
  if (sessionStart < ACC_START_DATE) {
    return null;
  }

  // Si tenemos acelerometría completa XYZ:
  if (accData && accData.x && accData.x.length > 50) {
    const fs = accData.fs || 50;
    const N = accData.x.length;
    const counts = { sentado: 0, parado: 0, en_movimiento: 0, otro: 0 };

    const step = Math.max(1, Math.round(fs));
    let totalSec = 0;

    for (let i = 0; i < N - step; i += step) {
      let sx = 0, sy = 0, sz = 0, sumM = 0;
      const mags = [];

      for (let j = i; j < i + step; j++) {
        const xi = accData.x[j];
        const yi = accData.y[j];
        const zi = accData.z[j];
        sx += xi; sy += yi; sz += zi;
        const m = Math.sqrt(xi * xi + yi * yi + zi * zi);
        mags.push(m);
        sumM += m;
      }

      const meanX = sx / step;
      const meanY = sy / step;
      const meanZ = sz / step;
      const meanM = sumM / step;

      let sumSq = 0;
      for (let j = 0; j < mags.length; j++) {
        const d = mags[j] - meanM;
        sumSq += d * d;
      }
      const motionRms = Math.sqrt(sumSq / mags.length);
      const isStepping = motionRms >= 32;

      const timeOffset = (i / fs) * 1000;
      const approxTime = (accData.startTime || readings[0].time) + timeOffset;
      const rMatch = readings.find(r => Math.abs(r.time - approxTime) < 1500);
      const hr = rMatch?.hr || null;

      const state = classifyInstantPosture({ x: meanX, y: meanY, z: meanZ, motionRms, isStepping, hr });
      counts[state]++;
      totalSec++;
    }

    return computePostureBreakdown(counts);
  }

  // Estimación a partir de lecturas de HR, pasos y duración (durSec ya calculado arriba)
  const walkSec = Math.min(durSec, Math.round(totalSteps / 1.6));
  const remainingSec = Math.max(0, durSec - walkSec);

  let restSec = 0;
  let activeSec = 0;
  readings.forEach(r => {
    if (r.hr && r.hr < 65) restSec++;
    else activeSec++;
  });
  const hrTotal = Math.max(1, restSec + activeSec);
  const restRatio = restSec / hrTotal;

  const isNight = (() => {
    const h = new Date(readings[0].time).getHours();
    return h >= 23 || h <= 7;
  })();

  const otroSec = isNight ? Math.round(remainingSec * restRatio * 0.75) : 0;
  const sentadoSec = Math.round((remainingSec - otroSec) * restRatio);
  const paradoSec = Math.max(0, remainingSec - otroSec - sentadoSec);

  return computePostureBreakdown({
    sentado: sentadoSec,
    parado: paradoSec,
    en_movimiento: walkSec,
    otro: otroSec,
  });
}
